import styled from '@emotion/styled';
import { format } from 'date-fns';
import { Formik } from 'formik';
import { FormikHelpers } from 'formik/dist/types';
import { ApplicationStatus } from 'modules/teams/domain/enums/application-status';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { upsertChampionshipPlayerApplication } from 'modules/teams/store/championship-teams/actions';
import { toastApplicationSaveError } from 'modules/teams/ui/utils';
import { msg } from '@lingui/core/macro';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useDispatch, useSelector } from 'react-redux';
import { trackEvent } from 'shared/analytics';
import { birthDateSchema } from 'shared/validators/date.validator';

import { AppDispatch } from 'store/store';
import UDFormInput from 'ud-form/components/input';
import { birthDateFormatter } from 'ud-form/components/input/domain/formatters/birth-date-formatter';
import UDButton from 'ud-ui/components/button';
import * as yup from 'yup';
import UDText from 'ud-ui/components/text';
import { CreateUserInputDto } from 'modules/teams/domain/dtos/create-user-input.dto';
import { Player } from 'modules/players/domain/interfaces/player';
import playersRepository from 'modules/players/domain/repositories/player.repository';
import { SuggestedPlayerList } from './create-team-player-form-fields';
import { TitleField } from './styles';
import { theme } from 'styles/theme/theme';

const CreateTeamPlayerFormWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
`;

const CreateTeamPlayerFormContainer = styled.div`
  padding: 0;
  border-radius: 8px;
  margin-bottom: 23px;
`;

const CreateTeamPlayerAutocompleteContainer = styled.div`
  position: relative;
`;

const CreateTeamPlayerFormFields = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: end;
  margin-top: 28px;
  gap: 16px;
`;

const teamPlayerValidator = yup.object().shape({
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  middleName: yup.string().optional(),
  birthDate: birthDateSchema,
});

interface CreateTeamPlayerFormProps {
  teamId: number;
  championshipId: number;
  existPlayerIds: number[];
}

type TeamPlayerValues = {
  lastName: string;
  firstName: string;
  middleName: string;
  birthDate: string | null;
  time?: number;
};

const CreateTeamPlayerForm = ({ teamId, championshipId }: CreateTeamPlayerFormProps) => {
  const { t } = useLingui();
  const tournamentId = useSelector(authStoreUserTournamentId);

  const [initialValues, setInitialValues] = useState<TeamPlayerValues>({
    firstName: '',
    lastName: '',
    middleName: '',
    birthDate: '',
  });

  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const middleNameRef = useRef<HTMLInputElement>(null);
  const birthDateRef = useRef<HTMLInputElement>(null);
  const suggestionListRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch<AppDispatch>();
  const [isSuggestLoading, setIsSuggestLoading] = useState<boolean>(false);
  const [suggestQuery, setSuggestQuery] = useState<string>('');
  const [users, setUsers] = useState<Player[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [suggestionVisible, setSuggestionVisible] = useState<boolean>(true);

  useEffect(() => {
    if (tournamentId && suggestQuery.length > 2) {
      setIsSuggestLoading(true);

      playersRepository
        .suggestTournamentPlayers(tournamentId, suggestQuery)
        .then((items) => setUsers(items))
        .finally(() => setIsSuggestLoading(false));
    } else if (suggestQuery.length <= 2 && users.length) {
      setUsers([]);
    }
  }, [suggestQuery, tournamentId, users.length]);

  const handleChange = useCallback(() => {
    const nameParts = [];

    if (lastNameRef.current) {
      nameParts.push(lastNameRef.current.value);
    }

    if (firstNameRef.current) {
      nameParts.push(firstNameRef.current.value);
    }

    if (middleNameRef.current) {
      nameParts.push(middleNameRef.current.value);
    }

    const name = nameParts
      .filter((value): value is string => Boolean(value))
      .map((item) => item.trim())
      .join(' ');

    setSelectedUserId(null);

    if (suggestQuery !== name) {
      setSuggestQuery(name);
    }
  }, [suggestQuery]);

  const handleFocus = useCallback(() => {
    setSuggestionVisible(true);
  }, []);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement, Element>) => {
    if (e.relatedTarget && suggestionListRef.current && e.relatedTarget === suggestionListRef.current) {
      return;
    }

    setSuggestionVisible(false);
  }, []);

  const handleSelectUser = useCallback((user: Player) => {
    const { id, surname: lastName, name: firstName } = user;
    const middleName = user.middleName || '';
    const birthDate = user.birthDate ? format(new Date(user.birthDate), 'dd.MM.uuuu') : null;

    setUsers([]);
    setSelectedUserId(id);
    setInitialValues({
      lastName,
      firstName,
      middleName,
      birthDate,
      time: Date.now(),
    });
  }, []);

  const handleSubmit = useCallback(
    // Промис — Formik: по нему «Заявить игрока» заперта до ответа (IMGM-1561).
    (values: TeamPlayerValues, { resetForm }: FormikHelpers<TeamPlayerValues>) => {
      const createUserOrUpdateApplication: CreateUserInputDto = {
        name: values.firstName,
        surname: values.lastName,
        middleName: values.middleName,
        birthDate: values.birthDate ?? undefined,
        state: ApplicationStatus.PENDING,
      };

      if (selectedUserId) {
        createUserOrUpdateApplication.userId = selectedUserId;
      }

      return (
        dispatch(
          upsertChampionshipPlayerApplication({
            teamId,
            championshipId,
            createUserOrUpdateApplication,
          }),
        )
          .unwrap()
          .then(() => {
            StepperMainTool.setByPath('stepper:main:createPlayer', true);
            trackEvent('Player Created', { source: 'team-card' });
            resetForm({
              values: {
                firstName: '',
                lastName: '',
                middleName: '',
                birthDate: '',
              },
            });
          })
          // Отказ — тостом с причиной (и 7016 «уже заявлен в другой команде»); код 15 повтора helper глушит сам.
          .catch((error) => toastApplicationSaveError(error, msg`Не удалось заявить игрока`))
      );
    },
    [championshipId, dispatch, selectedUserId, teamId],
  );

  return (
    <CreateTeamPlayerFormWrapper>
      <Formik enableReinitialize initialValues={initialValues} validationSchema={teamPlayerValidator} onSubmit={handleSubmit}>
        {(formProps) => (
          <form autoComplete="off" onSubmit={formProps.handleSubmit}>
            <CreateTeamPlayerFormContainer>
              <CreateTeamPlayerAutocompleteContainer>
                <CreateTeamPlayerFormFields>
                  <TitleField width={220}>
                    <UDText type={'subhead'} style={{ color: theme.colors.SurfaceRomanSilver30 }}>
                      <Trans>Фамилия*</Trans>
                    </UDText>
                    <UDFormInput
                      autoComplete="none"
                      name="lastName"
                      placeholder={t`Фамилия`}
                      ref={lastNameRef}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={(event) => handleBlur(event)}
                    />
                  </TitleField>
                  <TitleField width={220}>
                    <UDText type={'subhead'} style={{ color: theme.colors.SurfaceRomanSilver30 }}>
                      <Trans>Имя*</Trans>
                    </UDText>
                    <UDFormInput
                      autoComplete="none"
                      name="firstName"
                      placeholder={t`Имя`}
                      ref={firstNameRef}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      style={{ height: 36 }}
                    />
                  </TitleField>
                  <TitleField width={220}>
                    <UDText type={'subhead'} style={{ color: theme.colors.SurfaceRomanSilver30 }}>
                      <Trans>Отчество</Trans>
                    </UDText>
                    <UDFormInput
                      autoComplete="none"
                      name="middleName"
                      placeholder={t`Отчество`}
                      ref={middleNameRef}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      style={{ height: 36 }}
                    />
                  </TitleField>
                  <TitleField width={146}>
                    <UDText type={'subhead'} style={{ color: theme.colors.SurfaceRomanSilver30 }}>
                      <Trans>Дата рождения*</Trans>
                    </UDText>
                    <UDFormInput
                      name="birthDate"
                      placeholder={t`Дата рождения`}
                      mask={t({ message: 'дд.мм.гггг', comment: 'Маска поля даты: день, месяц, год' })}
                      ref={birthDateRef}
                      formatter={birthDateFormatter}
                      parser={birthDateFormatter}
                      style={{ height: 36 }}
                    />
                  </TitleField>
                  <UDButton
                    style={{ marginLeft: 'auto', width: 186, height: 36 }}
                    type="submit"
                    variant="primary"
                    disabled={!formProps.dirty}
                    // Загрузка подсказок по ФИО — запрос идёт не отсюда.
                    loading={isSuggestLoading}
                  >
                    {t`Заявить игрока`}
                  </UDButton>
                </CreateTeamPlayerFormFields>

                {suggestionVisible && <SuggestedPlayerList ref={suggestionListRef} users={users} onClick={handleSelectUser} />}
              </CreateTeamPlayerAutocompleteContainer>
            </CreateTeamPlayerFormContainer>
          </form>
        )}
      </Formik>
    </CreateTeamPlayerFormWrapper>
  );
};

export default CreateTeamPlayerForm;
